import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { articleLd } from '../lib/seo';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, FileText, MessageCircle, Loader2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import { api, resolveImg } from '../lib/api';
import { openWhatsApp } from '../lib/wa';

const formatDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const UpdateDetail = () => {
  const { slug } = useParams();
  const [update, setUpdate] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/updates/${slug}`).then(({ data }) => setUpdate(data)).catch(() => setUpdate(null)),
      api.get('/updates').then(({ data }) => setMore(data.filter((u) => u.slug !== slug).slice(0, 3))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-ktc-green" size={36} /></div>;
  }

  if (!update) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl text-ktc-green">Update not found</h1>
        <Link to="/updates" className="inline-flex items-center gap-2 text-ktc-gold font-medium mt-6"><ArrowLeft size={16} /> Back to updates</Link>
      </div>
    );
  }

  return (
    <div>
      <Seo
        title={update.title}
        description={(update.body || '').slice(0, 155)}
        path={`/updates/${update.slug}`}
        image={update.image ? resolveImg(update.image) : undefined}
        type="article"
        jsonLd={articleLd(update, update.image ? resolveImg(update.image) : undefined)}
      />
      <div className="bg-ktc-cream/50 border-b border-ktc-cream">
        <div className="max-w-3xl mx-auto px-6 py-4 text-sm text-ktc-charcoal/60 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-ktc-green">Home</Link> /
          <Link to="/updates" className="hover:text-ktc-green">Updates</Link> /
          <span className="text-ktc-green font-medium">{update.category}</span>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-14">
        <Reveal>
          <div className="flex items-center gap-3 text-xs">
            <span className="bg-ktc-gold text-ktc-greenDark font-semibold px-3 py-1 rounded-full">{update.category}</span>
            <span className="flex items-center gap-1.5 text-ktc-charcoal/55"><Calendar size={13} /> {formatDate(update.date)}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ktc-green mt-4 leading-tight">{update.title}</h1>
          {update.image && (
            <div className="img-zoom rounded-2xl overflow-hidden shadow-xl mt-8">
              <img src={resolveImg(update.image)} alt={update.title} className="w-full h-[380px] object-cover" />
            </div>
          )}
          <p className="text-ktc-charcoal/80 mt-8 leading-[1.8] text-[16px] whitespace-pre-line">{update.body}</p>

          <div className="flex flex-wrap gap-3 mt-10 border-t border-ktc-cream pt-8">
            <Link to="/quote" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-7 py-3.5 rounded-md transition-colors"><FileText size={17} /> Request a Quote</Link>
            <button type="button" onClick={() => openWhatsApp()} data-testid="update-whatsapp-button" className="inline-flex items-center gap-2 border border-ktc-green/25 hover:border-ktc-green text-ktc-green font-medium px-7 py-3.5 rounded-md transition-colors"><MessageCircle size={17} /> WhatsApp</button>
          </div>
        </Reveal>
      </article>

      {more.length > 0 && (
        <section className="bg-ktc-cream/50">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <h2 className="font-serif text-3xl font-semibold text-ktc-green mb-8">More Updates</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {more.map((u) => (
                <Link key={u.id} to={`/updates/${u.slug}`} className="group bg-ktc-ivoryLight rounded-xl overflow-hidden border border-ktc-cream card-lift">
                  <div className="img-zoom h-40"><img src={resolveImg(u.image)} alt={u.title} className="w-full h-full object-cover" /></div>
                  <div className="p-5">
                    <span className="text-[11px] uppercase tracking-wider text-ktc-gold font-semibold">{u.category}</span>
                    <h3 className="font-serif text-lg font-semibold text-ktc-green mt-1 leading-snug">{u.title}</h3>
                    <span className="inline-flex items-center gap-1.5 text-ktc-gold font-medium text-sm mt-3 group-hover:gap-2.5 transition-all">Read <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default UpdateDetail;
